import { TeoError } from '../errors/TeoError';
import type {
  APITeoMarker,
  LanguageToolCheckData,
  LanguageToolTextBlock,
  TextBlock,
} from '../TeoApi';
import { TeoEventTarget } from '../utility/TeoEventTarget';
import type { TeoText } from './TeoText';

export type SpellCheckHandlerEvents = {
  currentMatchesChanged: (detail: SpellCheckMatch[]) => void;
  spellCheckReceived: (detail: { matches: SpellCheckMatch[]; markers: APITeoMarker[] }) => void;
  matchFocused: (detail: SpellCheckMatch | undefined) => void;
  loading: (detail: boolean) => void;
};

export type SpellCheckImprovement = {
  key: string;
  suggestion: string;
  variant: 'suggestion' | 'delete';
  useAi?: boolean;
};

export type SpellCheckMatch = {
  message: string;
  variantLabel: string;
  improvements: SpellCheckImprovement[];
  key: string;
  variant: SpellingGroup;
  offset: number;
  length: number;
};

export type SpellingGroup = 'spelling' | 'grammar';

export class SpellCheckHandler {
  public events = new TeoEventTarget<SpellCheckHandlerEvents>();
  public maxSuggestions = 10; // Maximum number of suggestions per match
  public matches: SpellCheckMatch[] = [];
  public currentMatches: SpellCheckMatch[] = [];
  public markers: APITeoMarker[] = [];
  private markerNames = {
    spelling: ['spellcheck-spelling'],
    grammar: ['spellcheck-grammar'],
  };
  private _focusedMatch?: SpellCheckMatch;
  private relationOffset = 100000; // Offset for marker relations to avoid conflicts with other markers

  constructor(private text: TeoText) {
    // Listen for marker clicks
    this.text.markers.events.addEventListener(
      'markerClicked',
      (detail: { markerId: string; marker: APITeoMarker }) => {
        this.handleMarkerClick(detail);
      },
    );

    this.text.ui.events.addEventListener('spellCheckGroup', () => {
      this.updateCurrentMatches();
    });
  }

  get focusedMatch(): SpellCheckMatch | undefined {
    return this._focusedMatch;
  }

  async check() {
    return this.text.store.withLoading(() => this.runCheck(), 'Rechtschreibung prüfen...');
  }

  private async runCheck() {
    this.events.triggerEvent('loading', true);
    const result = await this.text.core.api.getLanguageToolCheck({
      data: {
        annotation: this.generateLTTextBlocks(),
      },
      language: 'de-DE',
    });

    if (TeoError.isError(result)) {
      console.error('Spell check error:', result.message);
      this.text.core.notifications.add({
        title: 'Rechtschreibprüfung fehlgeschlagen',
        message: result.message,
        mode: 'error',
      });
    } else {
      console.log('Spell check result:', result);
      this.matches = this.createMatchesFromData(result);
      this.markers = this.createMarkersFromMatches(this.matches);
      this.updateCurrentMatches();
      this.events.triggerEvent('spellCheckReceived', {
        matches: this.matches,
        markers: this.markers,
      });
    }
    this.events.triggerEvent('loading', false);
  }

  updateCurrentMatches() {
    const group = this.text.ui.state.spellCheckGroup;
    if (group) {
      this.currentMatches = this.matches.filter((match) => match.variant === group);
      this.text.markers.setMarkers([], this.markerNames[group as SpellingGroup] || []);
    } else {
      this.currentMatches = [...this.matches];
      const allMarkerNames = Object.values(this.markerNames).flat();
      this.text.markers.setMarkers([], allMarkerNames);
    }
    this.events.triggerEvent('currentMatchesChanged', this.currentMatches);
  }

  focusMatch(match: SpellCheckMatch | undefined) {
    if (this._focusedMatch === match) return;
    this._focusedMatch = match;
    this.text.markers.focusMarkers(match ? [match.key] : []);
    this.events.triggerEvent('matchFocused', match);
  }

  getMatchByKey(key: string): SpellCheckMatch | undefined {
    console.log('Searching for match with key:', key, this.matches);
    return this.matches.find((match) => match.key === key);
  }

  useImprovement(suggestionKey: string): void {
    if (!this._focusedMatch) {
      console.error('No focused match to apply suggestion.');
      return;
    }
    const suggestion = this._focusedMatch.improvements.find((s) => s.key === suggestionKey);
    if (!suggestion) {
      console.error('No suggestion found for key:', suggestionKey);
      return;
    }
    this.replaceInText(this._focusedMatch.key, suggestion);
  }

  replaceInText(matchKey: string, suggestion: SpellCheckImprovement): void {
    this.text.markers.replaceMarkerContent(matchKey, suggestion.suggestion);
  }

  private createMarkersFromMatches(matches: SpellCheckMatch[]): APITeoMarker[] {
    return matches.map((match) => ({
      marker: `spellcheck-${match.variant}`,
      offset: match.offset,
      length: match.length,
      relation: match.key,
      groupName: 'spellcheck',
      color: 'error',
    }));
  }

  private handleMarkerClick(detail: { markerId: string; marker: APITeoMarker }) {
    if (detail.marker.groupName !== 'spellcheck') return;
    if (detail.marker.relation === null) return;
    const matchIdx = detail.marker.relation;
    const match = this.getMatchByKey(matchIdx.toString());
    this.focusMatch(match);
  }

  private generateLTTextBlocks(): LanguageToolTextBlock[] {
    if (this.text.getTextArray === null) {
      console.error('SpellCheckHandler: getTextArray is not defined on the text object.');
      return [];
    }
    const blocks = this.text.getTextArray();

    return blocks.map((block) => {
      const ltBlock: LanguageToolTextBlock = {};
      if (block.type === 'text') {
        ltBlock.text = block.string;
      } else if (block.type === 'linebreak') {
        ltBlock.markup = this.createFakeMarkup(block);
        ltBlock.interpretAs = '\n';
      } else if (block.type === 'whitespace') {
        ltBlock.markup = this.createFakeMarkup(block);
        ltBlock.interpretAs = ' ';
      } else if (block.type === 'paragraph') {
        ltBlock.markup = this.createFakeMarkup(block);
        ltBlock.interpretAs = '\n\n';
      } else if (block.type === 'ignore') {
        ltBlock.markup = this.createFakeMarkup(block);
      }
      if (typeof block.interpret_as !== 'undefined') {
        if (ltBlock.text !== undefined) {
          ltBlock.markup = this.createFakeMarkup(block);
          delete ltBlock.text;
        }
        ltBlock.interpretAs = block.interpret_as;
      }
      return ltBlock;
    });
  }

  private createFakeMarkup(block: TextBlock): string {
    return block.length ? 'm'.repeat(block.length) : block.string;
  }

  public createMatchesFromData(detail: LanguageToolCheckData): SpellCheckMatch[] {
    const matches: SpellCheckMatch[] = [];
    for (const [index, match] of detail.matches.entries()) {
      const variantLabel = match.context.text.substring(
        match.context.offset,
        match.context.offset + match.context.length,
      );

      const improvements: SpellCheckImprovement[] = [];
      for (const [i, replacement] of match.replacements.slice(0, this.maxSuggestions).entries()) {
        improvements.push({
          key: i.toString(),
          suggestion: replacement.value,
          variant: 'suggestion',
        });
      }

      const group = this.getGroupName(match.rule.issueType);

      const scMatch: SpellCheckMatch = {
        message: match.message,
        variantLabel,
        improvements,
        key: (index + this.relationOffset).toString(),
        variant: group === 'Rechtschreibung' ? 'spelling' : 'grammar',
        offset: match.offset,
        length: match.length,
      };

      matches.push(scMatch);
    }

    return matches;
  }

  private getGroupName(type?: string): 'Rechtschreibung' | 'Grammatik' | 'Unbekannt' {
    if (!type) return 'Unbekannt';

    const rechtschreibung = new Set([
      'misspelling',
      'typographical',
      'characters',
      'whitespace',
      'locale-violation',
      'formatting',
      'inconsistent-entities',
      'numbers',
    ]);

    return rechtschreibung.has(type) ? 'Rechtschreibung' : 'Grammatik';
  }
}
